// src/widgets/ClassMembersModal.jsx
import { useEffect, useMemo, useState, useCallback } from "react"
import { X, Users, Trash2, LoaderCircle, Plus, Search, Briefcase, GraduationCap } from "lucide-react"
import Select from "react-select"
import { AnimatePresence, motion } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import LearnersPickerModal from "@/widgets/LearnersPickerModal"
import { getClassMembers, removeClassMember, getGroups, getJobTitles, getPrefixes } from "@/api/kuizy"
import DataTable from "@/widgets/DataTable"
import { formatDateTime } from "@/settings/master"

const phoneDisp = u => ((u.phone_code || "") + (u.phone || "")) || "-"

export default function ClassMembersModal({ classId, className, onClose }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [serverPaged, setServerPaged] = useState(false)

  const [alert, setAlert] = useState({ open: false, msg: "" })
  const [delItem, setDelItem] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [picker, setPicker] = useState(false)

  const [groups, setGroups] = useState([])
  const [jobTitles, setJobTitles] = useState([])
  const [prefixes, setPrefixes] = useState([])

  const [filterGroup, setFGroup] = useState("")
  const [filterJob, setFJob] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => parseInt(localStorage.getItem("dt:classMembers:perPage")) || 10)
  const [sortBy, setSortBy] = useState("joined_at")
  const [sortDir, setSortDir] = useState("desc")

  const [selectedMap, setSelectedMap] = useState({})
  const [selectedRows, setSelectedRows] = useState({})
  const [bulkConfirm, setBulkConfirm] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  useEffect(() => {
    localStorage.setItem("dt:classMembers:perPage", String(perPage))
  }, [perPage])

  useEffect(() => {
    Promise.all([getGroups(), getJobTitles(1), getPrefixes()]).then(([g, jt, pf]) => {
      setGroups(g.data || [])
      setJobTitles(jt.data || [])
      setPrefixes(pf.data || [])
    })
  }, [])

  const fullName = useCallback(u => {
    const pre = prefixes.find(p => p.code === u.prefix)?.name_th || u.prefix || ""
    const fn = u.name || ""
    const ln = u.last_name || ""
    return `${pre ? pre + " " : ""}${fn} ${ln}`.trim()
  }, [prefixes])

  const load = useCallback(() => {
    setLoading(true)
    getClassMembers(classId, {
      group_id: filterGroup || undefined,
      job_title: filterJob || undefined,
      searchText: search || undefined,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_dir: sortDir,
    })
      .then(res => {
        const d = res?.data
        if (d && typeof d === "object" && ("rows" in d || "total" in d)) {
          setServerPaged(true)
          setRows(d.rows || [])
          setTotal(d.total || 0)
        } else {
          const arr = Array.isArray(d) ? d : d?.data ?? []
          setServerPaged(false)
          setRows(arr)
          setTotal(Array.isArray(arr) ? arr.length : 0)
        }
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [classId, filterGroup, filterJob, search, page, perPage, sortBy, sortDir])

  useEffect(load, [load])

  const groupOptions = [{ value: "", label: "ทั้งหมด" }, ...groups.map(g => ({ value: String(g.id), label: g.name }))]

  const jobOptions = [
    { value: "", label: "ทั้งหมด" },
    ...jobTitles.map(j => ({ value: j.job_title, label: `${j.job_title} (${j.cnt})` })),
  ]

  const clientFiltered = useMemo(() => {
    if (serverPaged) return rows
    const q = search.trim().toLowerCase()
    const gSel = filterGroup
    const jSel = filterJob
    return (rows || []).filter(u => {
      const passText =
        !q ||
        fullName(u).toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        ((u.phone_code || "") + (u.phone || "")).toLowerCase().includes(q) ||
        (u.job_title || "").toLowerCase().includes(q)
      if (!passText) return false
      const passGroup =
        !gSel ||
        u.group_id === gSel ||
        (Array.isArray(u.group_ids) && u.group_ids.includes(gSel))
      if (!passGroup) return false
      const passJob = !jSel || (u.job_title || "") === jSel
      return passJob
    })
  }, [rows, serverPaged, search, filterGroup, filterJob, fullName])

  const sortedClient = useMemo(() => {
    if (serverPaged) return clientFiltered
    const dup = [...clientFiltered]
    const getter = (r, key) => {
      if (key === "full_name") return fullName(r)
      return r[key]
    }
    dup.sort((a, b) => {
      const va = getter(a, sortBy) ?? ""
      const vb = getter(b, sortBy) ?? ""
      if (va < vb) return sortDir === "asc" ? -1 : 1
      if (va > vb) return sortDir === "asc" ? 1 : -1
      return 0
    })
    return dup
  }, [clientFiltered, serverPaged, sortBy, sortDir, fullName])

  const effectiveTotal = serverPaged ? total : sortedClient.length
  const displayRows = useMemo(() => {
    if (serverPaged) return rows
    const start = (page - 1) * perPage
    return sortedClient.slice(start, start + perPage)
  }, [serverPaged, rows, sortedClient, page, perPage])

  const rowKey = useCallback((u) => `${u.class_id || classId}_${u.learner_id}`, [classId])
  const selectedSet = useMemo(() => new Set(Object.keys(selectedMap).filter(k => selectedMap[k])), [selectedMap])

  const onToggleRow = (key, row) => {
    setSelectedMap(s => {
      const n = { ...s }
      if (n[key]) {
        delete n[key]
        setSelectedRows(rm => { const t = { ...rm }; delete t[key]; return t })
      } else {
        n[key] = true
        setSelectedRows(rm => ({ ...rm, [key]: row }))
      }
      return n
    })
  }

  const onToggleAll = (keysOnPage, rowsOnPage = []) => {
    const all = keysOnPage.every(k => selectedSet.has(k))
    setSelectedMap(s => {
      const n = { ...s }
      if (all) keysOnPage.forEach(k => delete n[k])
      else keysOnPage.forEach(k => (n[k] = true))
      return n
    })
    setSelectedRows(rm => {
      const t = { ...rm }
      if (all) keysOnPage.forEach(k => delete t[k])
      else keysOnPage.forEach((k, i) => { t[k] = rowsOnPage[i] })
      return t
    })
  }

  const confirmDelete = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      await removeClassMember(classId, delItem.learner_id)
      load()
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      setDeleting(false)
      setDelItem(null)
    }
  }

  const confirmBulkDelete = async () => {
    const keys = Array.from(selectedSet)
    if (keys.length === 0) { setBulkConfirm(false); return }
    setBulkDeleting(true)
    try {
      const tasks = keys.map(k => {
        const r = selectedRows[k]
        if (!r) return Promise.resolve()
        return removeClassMember(classId, r.learner_id)
      })
      await Promise.all(tasks)
      setSelectedMap({})
      setSelectedRows({})
      load()
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      setBulkDeleting(false)
      setBulkConfirm(false)
    }
  }

  const columns = useMemo(() => [
    { key: "full_name", title: "ชื่อ–สกุล", sortable: true, sortKey: "first_name", render: (u) => (<span>{fullName(u)}</span>) },
    { key: "group_name", title: "กลุ่มผู้เรียน", headerClassName: "hidden md:table-cell", cellClassName: "hidden md:table-cell", render: (u) => u.group_name || "Member" },
    { key: "job_title", title: "ตำแหน่ง", sortable: true, headerClassName: "hidden sm:table-cell", cellClassName: "hidden sm:table-cell", render: (u) => u.job_title || "-" },
    { key: "email", title: "อีเมล", sortable: true, headerClassName: "hidden md:table-cell", cellClassName: "hidden md:table-cell" },
    { key: "phone", title: "โทรศัพท์", align: "center", sortable: false, render: (u) => phoneDisp(u) },
    { key: "joined_at", title: "เพิ่มเมื่อ", align: "right", sortable: true, headerClassName: "hidden lg:table-cell", cellClassName: "hidden lg:table-cell", render: (u) => formatDateTime(u.joined_at) },
    {
      key: "__actions",
      title: "จัดการ",
      align: "right",
      pinEnd: true,
      width: 80,
      render: (u) => (
        <button onClick={() => setDelItem(u)} className="text-red-600 hover:text-red-800" title="ลบ">
          <Trash2 size={16} />
        </button>
      )
    }
  ], [fullName])

  return (
    <AnimatePresence>
      <motion.div key="cmBack" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4">
        <motion.div key="cmCard" initial={{ y: 32, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 32, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[95vh] sm:w-[95%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
            <h2 className="flex items-center gap-2 text-sm sm:text-base font-semibold">
              <GraduationCap size={18} /> ผู้เรียนในห้องเรียน “{className}”
            </h2>
            <button onClick={onClose} className="text-gray-600 hover:text-primary">
              <X size={20} />
            </button>
          </header>

          <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
            <button onClick={() => setPicker(true)} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white text-sm rounded-lg px-3 py-1">
              <Plus size={14} /> เพิ่มผู้เรียน
            </button>
            <div className="flex-1" />
            <button
              disabled={selectedSet.size === 0}
              onClick={() => setBulkConfirm(true)}
              className="inline-flex items-center gap-1 text-sm rounded-lg px-3 py-1 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> ลบที่เลือก ({selectedSet.size})
            </button>
          </div>

          <div className="flex flex-wrap gap-4 items-end px-4 py-2 bg-info border-b border-gray-200">
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1 text-sm">
                <Users size={16} /> กลุ่มผู้เรียน
              </label>
              <Select className="w-44 rs-select" classNamePrefix="rs" options={groupOptions} value={groupOptions.find(o => o.value === filterGroup)} onChange={o => { setFGroup(o.value); setPage(1) }} isSearchable />
            </div>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1 text-sm">
                <Briefcase size={16} /> ตำแหน่งงาน
              </label>
              <Select className="w-48 rs-select" classNamePrefix="rs" options={jobOptions} value={jobOptions.find(o => o.value === filterJob)} onChange={o => { setFJob(o.value); setPage(1) }} isSearchable />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
              <label className="flex items-center gap-1 text-sm">
                <Search size={16} /> ค้นหา
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <div className="flex items-center gap-2">
                  <input type="text" placeholder="ค้นหา..." value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { setSearch(searchInput); setPage(1) } }} className="border border-gray-300 rounded-lg pl-10 px-3 py-2 focus:ring-2 focus:ring-primary text-sm flex-1" />
                  <button onClick={() => { setSearch(searchInput); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
                    <Search size={16} /> ค้นหา
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 p-3">
            <DataTable
              className="h-full min-h-0"
              columns={columns}
              rows={displayRows}
              loading={loading}
              total={effectiveTotal}
              page={page}
              perPage={perPage}
              sortBy={sortBy}
              sortDir={sortDir}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
              onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
              selectable
              rowKey={rowKey}
              selectedSet={selectedSet}
              onToggleRow={onToggleRow}
              onToggleAll={(keysOnPage, rowsOnPage) => onToggleAll(keysOnPage, rowsOnPage)}
            />
          </div>

          <AnimatePresence>
            {picker && (
              <LearnersPickerModal
                classId={classId}
                existingIds={new Set(rows.map(u => u.learner_id))}
                onSuccess={() => { setPicker(false); load() }}
                onClose={() => setPicker(false)}
              />

            )}
          </AnimatePresence>

          {delItem && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบ"
              message={`ลบ “${fullName(delItem)}” ?`}
              pending={deleting}
              onConfirm={confirmDelete}
              onCancel={() => setDelItem(null)}
            />
          )}

          {bulkConfirm && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบหลายรายการ"
              message={`ลบผู้เรียนที่เลือกจำนวน ${selectedSet.size} รายการ ?`}
              pending={bulkDeleting}
              onConfirm={confirmBulkDelete}
              onCancel={() => setBulkConfirm(false)}
            />
          )}

          {alert.open && (
            <ConfirmModal
              type="alert"
              title="แจ้งเตือน"
              message={alert.msg}
              onClose={() => setAlert({ open: false, msg: "" })}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
